import { Alert, Box, Button, Card, CardContent, CircularProgress, Link, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@diggens/api-client';
import { ICON_SIZES } from '../../constants/icons';

interface HubSpotStatus {
  configured: boolean;
  connected: boolean;
  connectedAt: string | null;
}

const HUBSPOT_QUERY_KEY = ['integrations', 'hubspot'] as const;

/** HubSpot's sprocket in its brand orange, from Simple Icons (CC0; https://simpleicons.org/?q=hubspot). */
export function HubSpotLogo() {
  return (
    <svg width={ICON_SIZES.lg} height={ICON_SIZES.lg} viewBox="0 0 24 24" fill="#FF7A59" aria-hidden="true">
      <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z" />
    </svg>
  );
}

/**
 * Settings > Connections card where a user connects their own HubSpot, so the BDR can read it and send them a weekday morning digest in
 * Slack. Connecting happens in a new tab; the status refetches when the user comes back to this one.
 */
export function HubSpotConnection() {
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: HUBSPOT_QUERY_KEY,
    queryFn: () => apiFetch<HubSpotStatus>('/integrations/hubspot'),
    // The app default (no refetch on focus, five minutes fresh) would keep showing "not connected" after the user connects in the other tab.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const connect = useMutation({
    mutationFn: () =>
      apiFetch<{ authorizeUrl: string }>('/integrations/hubspot/connect', {
        method: 'POST',
        body: JSON.stringify({ timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      }),
    onSuccess: ({ authorizeUrl }) => {
      window.open(authorizeUrl, '_blank', 'noopener,noreferrer');
    },
  });

  const disconnect = useMutation({
    mutationFn: () => apiFetch<void>('/integrations/hubspot', { method: 'DELETE' }),
    onSettled: () => {
      // A disconnect deletes the pending connect too, so its continue link would no longer work.
      connect.reset();
      queryClient.invalidateQueries({ queryKey: HUBSPOT_QUERY_KEY });
    },
  });

  if (status.isPending) {
    return (
      <Box>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (status.data && !status.data.configured) return <Typography color="text.secondary">HubSpot is not set up on this server yet.</Typography>;

  const connected = status.data?.connected === true;
  const connectedAt = status.data?.connectedAt ? new Date(status.data.connectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  return (
    <Card sx={{ maxWidth: 800 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <HubSpotLogo />
          <Typography variant="h6">HubSpot</Typography>
        </Box>

        {status.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load your HubSpot connection.
          </Alert>
        )}
        {connect.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not start the HubSpot connection. Try again.
          </Alert>
        )}
        {disconnect.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not disconnect HubSpot. Try again.
          </Alert>
        )}

        {connected ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography>{connectedAt ? `Connected on ${connectedAt}` : 'Connected'}</Typography>
            <Button variant="outlined" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
              Disconnect
            </Button>
          </Box>
        ) : (
          status.data && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography color="text.secondary">The BDR reads your HubSpot, read only, and sends your weekday morning digest in Slack.</Typography>
              <Button variant="contained" onClick={() => connect.mutate()} disabled={connect.isPending} sx={{ flexShrink: 0 }}>
                Connect HubSpot
              </Button>
            </Box>
          )
        )}
        {!connected && connect.data && (
          // Safari can block a tab opened after the request returns, so the same URL stays one click away.
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            HubSpot opens in a new tab. If it did not,{' '}
            <Link href={connect.data.authorizeUrl} target="_blank" rel="noopener noreferrer">
              continue to HubSpot
            </Link>
            .
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
